import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/config";

const GA4_RE = /^G-[A-Z0-9]{10}$/;
let gtagLoadPromise = null;

function loadGtag(measurementId) {
  if (window.gtag) return Promise.resolve();
  if (gtagLoadPromise) return gtagLoadPromise;

  gtagLoadPromise = new Promise((resolve, reject) => {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load gtag.js"));
    document.head.appendChild(script);
  });

  return gtagLoadPromise;
}

/**
 * useGoogleAnalytics
 *  - fetches the currently active GA4 Measurement ID from the backend
 *  - loads gtag.js only when: valid ID configured AND user has accepted analytics cookies
 *  - tracks page_view on every react-router route change
 *
 * @param {boolean} cookiesAccepted  true when user has opted in to analytics cookies
 */
export function useGoogleAnalytics(cookiesAccepted) {
  const location = useLocation();
  const [measurementId, setMeasurementId] = useState(null);
  const initialized = useRef(false);

  // Fetch GA4 ID once at mount
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/analytics")
      .then((r) => {
        if (!cancelled) setMeasurementId(r.data?.ga4_measurement_id || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Initialize gtag when ready
  useEffect(() => {
    if (!cookiesAccepted) return;
    if (!measurementId || !GA4_RE.test(measurementId)) return;
    if (initialized.current) return;

    loadGtag(measurementId).then(() => {
      window.gtag && window.gtag("js", new Date());
      window.gtag && window.gtag("config", measurementId, { send_page_view: false });
      initialized.current = true;
      // Initial page view
      window.gtag && window.gtag("event", "page_view", {
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname + window.location.search,
      });
    }).catch(() => {});
  }, [cookiesAccepted, measurementId]);

  // Track SPA route changes
  useEffect(() => {
    if (!cookiesAccepted) return;
    if (!measurementId || !initialized.current) return;
    if (!window.gtag) return;
    window.gtag("event", "page_view", {
      page_title: document.title,
      page_location: window.location.href,
      page_path: location.pathname + location.search,
    });
  }, [cookiesAccepted, measurementId, location.pathname, location.search]);
}
